/**
 * Dialog behaviour shared by Modal, Drawer and ConfirmDialog (ux/01 §3.6-§3.8,
 * §4.5; AUD-UXDS-07, AUD-RECL-08).
 *
 *  · one stack of open dialogs: only the TOP dialog reacts to Échap / F9, so a
 *    confirmation opened over a form never closes both;
 *  · the initial-focus order: [data-autofocus] (or an initialFocusRef), then a
 *    DOM [autofocus], then the first field, then the primary action — never
 *    the × (marked [data-dialog-close]);
 *  · Entrée in a single-line field triggers the primary action, except inside
 *    a form (native submit), a textarea, a combobox or an open popup.
 */
import { MAX_DIALOG_DEPTH } from "../config/ui.config.js";

// ─── Stack ───────────────────────────────────────────────────────────────────

const stack = [];

export function pushDialog(id) {
  if (!stack.includes(id)) stack.push(id);
  return stack.length <= MAX_DIALOG_DEPTH;
}

export function removeDialog(id) {
  const i = stack.lastIndexOf(id);
  if (i >= 0) stack.splice(i, 1);
}

export function isTopDialog(id) {
  return stack.length > 0 && stack[stack.length - 1] === id;
}

export function dialogDepth() {
  return stack.length;
}

// ─── Opener tracking ─────────────────────────────────────────────────────────
//
// Focus must return to the element that opened a dialog (§4.5). The trap's
// effect runs AFTER React has already honoured an `autoFocus` inside the
// dialog, so `document.activeElement` at that point is the dialog's own field.
// A capture-phase focusin listener remembers the last element focused outside
// any dialog; the trap reads it when the active element is already inside.

let lastOutside = null;
if (typeof document !== "undefined") {
  document.addEventListener(
    "focusin",
    (e) => {
      const t = e.target;
      if (t && t.closest && !t.closest('[role="dialog"],[role="alertdialog"]')) {
        lastOutside = t;
      }
    },
    true,
  );
}

/**
 * The element to give focus back to when a dialog closes.
 * @param {Element|null} container the dialog panel
 * @param {Element|null} active    document.activeElement when the trap starts
 */
export function resolveOpener(container, active, remembered = lastOutside) {
  const body = typeof document !== "undefined" ? document.body : null;
  if (active && container && !container.contains(active) && active !== body) {
    return active;
  }
  return remembered && remembered !== container ? remembered : null;
}

// ─── Initial focus ───────────────────────────────────────────────────────────

/** Editable fields, in the §4.5 sense (not buttons, not read-only inputs). */
export const FIELD_SELECTOR = [
  'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([disabled]):not([readonly])',
  "select:not([disabled])",
  "textarea:not([disabled]):not([readonly])",
  '[role="combobox"]:not([aria-disabled="true"])',
  '[contenteditable="true"]',
].join(",");

/** The dialog's primary action: an explicit marker, else the footer's primary button. */
export const PRIMARY_SELECTOR = [
  "[data-dialog-primary]:not([disabled])",
  "[data-dialog-footer] .btn.primary:not([disabled])",
].join(",");

/** The §4.5 order, first match wins. */
export const INITIAL_FOCUS_ORDER = Object.freeze([
  "[data-autofocus]",
  "[autofocus]",
  FIELD_SELECTOR,
  PRIMARY_SELECTOR,
]);

const TABBABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const isCloseButton = (el) =>
  Boolean(el.closest && el.closest("[data-dialog-close]"));

const defaultVisible = (el) =>
  el.offsetParent !== null || el.getClientRects?.().length > 0;

/**
 * @param {ParentNode} container  the dialog panel
 * @param {(el: Element) => boolean} [isVisible]
 * @returns {Element|null} where focus goes when the dialog opens
 */
export function pickInitialFocus(container, isVisible = defaultVisible) {
  if (!container) return null;
  for (const selector of INITIAL_FOCUS_ORDER) {
    const hit = Array.from(container.querySelectorAll(selector)).find(
      (el) => !isCloseButton(el) && isVisible(el),
    );
    if (hit) return hit;
  }
  // Nothing editable and no primary action: the first tabbable that is not
  // the ×; the × only when it is the one control there is.
  const tabbables = Array.from(container.querySelectorAll(TABBABLE)).filter(
    (el) => isVisible(el),
  );
  return tabbables.find((el) => !isCloseButton(el)) ?? tabbables[0] ?? null;
}

// ─── Entrée → primary action ─────────────────────────────────────────────────

const SINGLE_LINE_TYPES = new Set([
  "text",
  "search",
  "email",
  "tel",
  "url",
  "password",
  "number",
  "date",
  "",
]);

/**
 * Should this Entrée keydown trigger the dialog's primary action?
 * @param {KeyboardEvent} e
 */
export function isPrimaryEnter(e) {
  if (e.key !== "Enter" || e.defaultPrevented || e.isComposing) return false;
  if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return false;
  const t = e.target;
  if (!t || t.tagName !== "INPUT") return false;
  const type = (t.getAttribute("type") || "").toLowerCase();
  if (!SINGLE_LINE_TYPES.has(type)) return false;
  if (t.form) return false; // the form's own submit handles it
  if (t.getAttribute("role") === "combobox") return false;
  if (t.getAttribute("aria-expanded") === "true") return false;
  if (t.closest && t.closest('[role="grid"],[role="combobox"],[data-own-enter]')) {
    return false;
  }
  return true;
}

/** Is this an input event that means « the user typed data »? */
export function isTypedInput(target) {
  if (!target) return false;
  if (target.tagName === "TEXTAREA") return true;
  if (target.isContentEditable) return true;
  if (target.tagName !== "INPUT") return false;
  const type = (target.getAttribute("type") || "").toLowerCase();
  return SINGLE_LINE_TYPES.has(type);
}
